import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import CellTowerIcon from "@mui/icons-material/CellTower";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import * as Yup from "yup";
import ContactUs from "src/Components/Modals/ContactUs";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/providers/AuthProvider";
import { DASHBOARD_PATH } from "src/constants/paths";
import { getApiErrorMessage } from "src/utils/apiError";

type Mode = "signin" | "register";

const MIN_PASSWORD_LENGTH = 10;

const safeNextPath = (next: string | null) =>
  next && next.startsWith("/") && !next.startsWith("//") ? next : DASHBOARD_PATH;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "rgba(11, 15, 23, 0.6)",
    borderRadius: "12px",
  },
};

const PasswordField: React.FC<TextFieldProps> = (props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      {...props}
      type={visible ? "text" : "password"}
      sx={fieldSx}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={() => setVisible((current) => !current)}
                aria-label={visible ? t("auth.hidePassword") : t("auth.showPassword")}
                aria-pressed={visible}
              >
                {visible ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

const ServerError: React.FC<{ message: string | null }> = ({ message }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (message) ref.current?.focus();
  }, [message]);
  if (!message) return null;
  return (
    <Alert
      ref={ref}
      tabIndex={-1}
      severity="error"
      role="alert"
      sx={{ borderRadius: "12px", outline: "none" }}
    >
      {message}
    </Alert>
  );
};

const SubmitButton: React.FC<{ submitting: boolean; label: string }> = ({
  submitting,
  label,
}) => (
  <Button
    type="submit"
    variant="contained"
    size="large"
    fullWidth
    disabled={submitting}
    startIcon={
      submitting ? <CircularProgress size={18} color="inherit" /> : undefined
    }
    sx={{
      py: 1.4,
      borderRadius: "12px",
      boxShadow: "0 10px 25px rgba(124, 58, 237, 0.3)",
    }}
  >
    {label}
  </Button>
);

const SignInForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email(t("auth.validation.emailInvalid"))
        .required(t("auth.validation.emailRequired")),
      password: Yup.string().required(t("auth.validation.passwordRequired")),
    }),
    onSubmit: async (values) => {
      setServerError(null);
      try {
        await login({ email: values.email.trim(), password: values.password });
        onSuccess();
      } catch (error) {
        setServerError(
          getApiErrorMessage(error, t, "auth.signInError")
        );
      }
    },
  });

  const fieldError = (name: "email" | "password") =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : undefined;

  return (
    <Box component="form" noValidate onSubmit={formik.handleSubmit}>
      <Stack spacing={2.5}>
        <ServerError message={serverError} />
        <TextField
          id="signin-email"
          name="email"
          label={t("auth.email")}
          type="email"
          autoComplete="email"
          fullWidth
          required
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(fieldError("email"))}
          helperText={fieldError("email")}
          sx={fieldSx}
        />
        <PasswordField
          id="signin-password"
          name="password"
          label={t("auth.password")}
          autoComplete="current-password"
          fullWidth
          required
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(fieldError("password"))}
          helperText={fieldError("password")}
        />
        <SubmitButton submitting={formik.isSubmitting} label={t("auth.logIn")} />
      </Stack>
    </Box>
  );
};

const RegisterForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const formik = useFormik({
    initialValues: {
      registrationCode: "",
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      registrationCode: Yup.string()
        .trim()
        .required(t("auth.validation.codeRequired")),
      name: Yup.string().trim().required(t("auth.validation.nameRequired")),
      email: Yup.string()
        .trim()
        .email(t("auth.validation.emailInvalid"))
        .required(t("auth.validation.emailRequired")),
      password: Yup.string()
        .min(
          MIN_PASSWORD_LENGTH,
          t("auth.validation.passwordMin", { count: MIN_PASSWORD_LENGTH })
        )
        .required(t("auth.validation.passwordRequired")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], t("auth.validation.passwordMismatch"))
        .required(t("auth.validation.confirmRequired")),
    }),
    onSubmit: async (values) => {
      setServerError(null);
      try {
        await register({
          registrationCode: values.registrationCode.trim(),
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password,
        });
        onSuccess();
      } catch (error) {
        setServerError(
          getApiErrorMessage(error, t, "auth.registerError")
        );
      }
    },
  });

  type Field = keyof typeof formik.values;
  const fieldError = (name: Field) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : undefined;
  const fieldProps = (name: Field) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: Boolean(fieldError(name)),
    fullWidth: true,
    required: true,
  });

  return (
    <Box component="form" noValidate onSubmit={formik.handleSubmit}>
      <Stack spacing={2.5}>
        <ServerError message={serverError} />
        <TextField
          id="register-code"
          label={t("auth.registrationCode")}
          autoComplete="off"
          {...fieldProps("registrationCode")}
          helperText={
            fieldError("registrationCode") ||
            t("auth.registrationCodeHelp")
          }
          sx={fieldSx}
        />
        <TextField
          id="register-name"
          label={t("auth.fullName")}
          autoComplete="name"
          {...fieldProps("name")}
          helperText={fieldError("name")}
          sx={fieldSx}
        />
        <TextField
          id="register-email"
          label={t("auth.workEmail")}
          type="email"
          autoComplete="email"
          {...fieldProps("email")}
          helperText={fieldError("email")}
          sx={fieldSx}
        />
        <PasswordField
          id="register-password"
          label={t("auth.password")}
          autoComplete="new-password"
          {...fieldProps("password")}
          helperText={
            fieldError("password") ||
            t("auth.passwordHelp", { count: MIN_PASSWORD_LENGTH })
          }
        />
        <PasswordField
          id="register-confirm-password"
          label={t("auth.confirmPassword")}
          autoComplete="new-password"
          {...fieldProps("confirmPassword")}
          helperText={fieldError("confirmPassword")}
        />
        <SubmitButton
          submitting={formik.isSubmitting}
          label={t("auth.createAccount")}
        />
      </Stack>
    </Box>
  );
};

export default function SignIn() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode =
    searchParams.get("mode") === "register" ? "register" : "signin";
  const nextPath = safeNextPath(searchParams.get("next"));

  if (status === "authenticated") {
    return <Navigate to={nextPath} replace />;
  }

  const setMode = (nextMode: Mode) => {
    const params = new URLSearchParams(searchParams);
    if (nextMode === "register") {
      params.set("mode", "register");
    } else {
      params.delete("mode");
    }
    setSearchParams(params, { replace: true });
  };

  const handleSuccess = () => navigate(nextPath, { replace: true });

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 6, sm: 10 },
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(124, 58, 237, 0.18), transparent 70%)",
          pointerEvents: "none",
        },
      }}
    >
      <Helmet>
        <title>{t("auth.pageTitle")}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 440,
          p: { xs: 3, sm: 4 },
          borderRadius: "20px",
          bgcolor: "#0f172a",
          border: "1px solid rgba(30, 41, 59, 1)",
          boxShadow: "0 25px 50px rgba(2, 6, 23, 0.5)",
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              aria-hidden="true"
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                background: "linear-gradient(135deg, #7c3aed, #6366f1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 10px 25px rgba(124, 58, 237, 0.3)",
              }}
            >
              <CellTowerIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                component="h1"
                sx={{ fontSize: "1.75rem", fontWeight: 800, color: "#fff" }}
              >
                {mode === "signin" ? t("auth.welcomeBack") : t("auth.createYourAccount")}
              </Typography>
              <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
                {mode === "signin"
                  ? t("auth.signInSubtitle")
                  : t("auth.registerSubtitle")}
              </Typography>
            </Box>
          </Stack>

          <Tabs
            value={mode}
            onChange={(_event, value: Mode) => setMode(value)}
            variant="fullWidth"
            aria-label={t("auth.tabsLabel")}
            sx={{
              minHeight: 0,
              p: 0.5,
              borderRadius: 999,
              bgcolor: "rgba(11, 15, 23, 0.8)",
              border: "1px solid rgba(30, 41, 59, 1)",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                minHeight: 40,
                borderRadius: 999,
                color: "#cbd5e1",
                fontWeight: 600,
                transition: "background-color 0.2s ease, color 0.2s ease",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#fff",
                bgcolor: "#7c3aed",
              },
            }}
          >
            <Tab
              value="signin"
              label={t("auth.logIn")}
              id="auth-tab-signin"
              aria-controls="auth-panel-signin"
            />
            <Tab
              value="register"
              label={t("auth.createAccount")}
              id="auth-tab-register"
              aria-controls="auth-panel-register"
            />
          </Tabs>

          <Box
            role="tabpanel"
            id={`auth-panel-${mode}`}
            aria-labelledby={`auth-tab-${mode}`}
          >
            {mode === "signin" ? (
              <SignInForm onSuccess={handleSuccess} />
            ) : (
              <RegisterForm onSuccess={handleSuccess} />
            )}
          </Box>

          {mode === "register" && (
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("auth.noCodePrompt")}
              </Typography>
              <ContactUs
                buttonLabel={t("auth.requestCode")}
                buttonLabelMobile={t("auth.requestCodeShort")}
                dialogTitle={t("auth.requestCode")}
                defaultMessage={t("auth.requestCodeMessage")}
                analyticsContext={{ source: "login_register" }}
              />
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
